import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Typography,
  FormLabel,
  TextField,
} from '@mui/material_modern'
import { v4 as uuidv4 } from 'uuid'
import { useSendEmailMutation } from 'services/email'
import { dispatchSnackbar } from 'utils/helpers'
import { useDispatch, useSelector } from 'react-redux'
import { useSendOutboundTicketEmailMutation } from 'gen/rtk-openapi'
import { EMAIL_TYPE, PDF_TYPE } from 'utils/constants'

const EmailModal = ({
  open,
  onClose,
  recipients = [] as string[],
  pdfTypeOptions = [] as PDF_TYPE[],
  defaultPdfType,
  entityId,
  allowMultiple = false,
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>(recipients.length > 0 ? [recipients[0]] : [])
  const [pdfType, setPdfType] = useState(defaultPdfType || pdfTypeOptions[0])
  const [selectedDocuments, setSelectedDocuments] = useState<PDF_TYPE[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [isCustomSelected, setIsCustomSelected] = useState(false)
  const [customEmail, setCustomEmail] = useState('')
  const [includeImages, setIncludeImages] = useState(false)

  const dispatch = useDispatch()
  const [sendEmail, { isLoading: isEmailLoading }] = useSendEmailMutation()
  const [sendOutboundTicketEmail, { isLoading: isOutboundTicketEmailLoading }] = useSendOutboundTicketEmailMutation()
  const isLoading = isEmailLoading || isOutboundTicketEmailLoading

  const pdfTypeNames = {
    IBT_PURCHASE_TICKET: 'Purchase Ticket',
    IBT_SCALE_TICKET: 'Scale Ticket',
    BILL_SETTLEMENT_DOC: 'Bill Settlement Document',
    OBT_PACKING_LIST: 'Packing List',
    OBT_BILL_OF_LADING: 'Bill of Lading',
    INVOICE: 'Invoice',
    PAYMENT_RECEIPT: 'Payment Receipt',
  }

  const mapPdfTypeToEmailType = (docTypes: PDF_TYPE[]): EMAIL_TYPE | null => {
    if (docTypes.length === 1) {
      const docType = docTypes[0]
      switch (docType) {
        case PDF_TYPE.OBT_BILL_OF_LADING:
          return EMAIL_TYPE.OBT_BILL_OF_LADING
        case PDF_TYPE.OBT_PACKING_LIST:
          return EMAIL_TYPE.OBT_PACKING_LIST
        default:
          return null
      }
    }
    if (docTypes.length === 2) {
      if (docTypes.includes(PDF_TYPE.OBT_BILL_OF_LADING) && docTypes.includes(PDF_TYPE.OBT_PACKING_LIST)) {
        return EMAIL_TYPE.OBT_PL_AND_BOL
      }
    }
    return null
  }

  const handleSendEmail = () => {
    const finalRecipients = isCustomSelected && customEmail ? [...selectedRecipients, customEmail] : selectedRecipients
    const uniqueRecipients = Array.from(new Set(finalRecipients)) // Sendgrid does not allow duplicates in recipients array
    if (allowMultiple) {
      const emailType = mapPdfTypeToEmailType(selectedDocuments)
      if (!emailType) {
        dispatchSnackbar(dispatch, 'error', 'Email Type could not be determined.')
        return
      }
      sendOutboundTicketEmail({
        body: {
          recipients: uniqueRecipients,
          emailType,
          idempotencyKey: uuidv4(),
          entityId,
          includeImages,
        },
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', `Email sent to ${uniqueRecipients.join(', ')}`)
          onClose()
        })
        .catch(error => {
          const errorMessage = error?.data?.error || 'An error occurred while sending the email.'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    } else {
      sendEmail({
        body: {
          recipients: uniqueRecipients,
          pdfType,
          idempotencyKey: uuidv4(),
          entityId,
          includeImages,
        },
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', `Email sent to ${uniqueRecipients.join(', ')}`)
          onClose()
        })
        .catch(error => {
          const errorMessage = error?.data?.error || 'An error occurred while sending the email.'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    }
  }

  const singlePdfType = pdfTypeOptions.length === 1

  const handleRecipientChange = email => {
    setSelectedRecipients(prev => (prev.includes(email) ? prev.filter(r => r !== email) : [...prev, email]))
  }

  const handlePdfTypeChange = pdfType => {
    setSelectedDocuments(prev => (prev.includes(pdfType) ? prev.filter(r => r !== pdfType) : [...prev, pdfType]))
  }

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedRecipients([]) // Deselect all
    } else {
      setSelectedRecipients([...recipients]) // Select all
    }
    setSelectAll(!selectAll)
  }

  const handleCustomEmailChange = e => {
    setCustomEmail(e.target.value)
  }

  const toggleCustomEmail = () => {
    setIsCustomSelected(!isCustomSelected)
    if (!isCustomSelected) {
      setCustomEmail('')
    }
  }

  const isFormValid = () => {
    return areRecipientsValid() && areDocumentsValid()
  }

  const areRecipientsValid = () => {
    return selectedRecipients.length > 0 || (isCustomSelected && isValidEmail(customEmail))
  }

  const areDocumentsValid = () => {
    // if we are allowing multiple documents to be sent, we need non-empty
    return (allowMultiple && selectedDocuments.length > 0) || !allowMultiple
  }

  // Update "Select All" state when all recipients are selected
  useEffect(() => {
    setSelectAll(selectedRecipients.length === recipients.length && recipients.length > 0)
  }, [selectedRecipients, recipients.length])

  useEffect(() => {
    if (recipients.length > 0 && selectedRecipients.length === 0) {
      setSelectedRecipients([recipients[0]])
    }
  }, [recipients])

  const isValidEmail = email => /\S+@\S+\.\S+/.test(email)

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{singlePdfType ? `Send ${pdfTypeNames[pdfType]}` : 'Send Email'}</DialogTitle>
      <DialogContent>
        {!singlePdfType && !allowMultiple && (
          <Grid item style={{ marginBottom: '8px' }}>
            <FormLabel component="legend" style={{ marginBottom: '8px' }}>
              What would you like to send?
            </FormLabel>
            <ToggleButtonGroup
              value={pdfType}
              exclusive
              fullWidth
              onChange={(_, newPdfType) => setPdfType(newPdfType)}
              aria-label="PDF Type"
            >
              {pdfTypeOptions.map(type => (
                <ToggleButton key={type} value={type} aria-label={type}>
                  {pdfTypeNames[type]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
        )}
        {!singlePdfType && allowMultiple && (
          <Grid item style={{ marginBottom: '8px' }}>
            <FormLabel component="legend" style={{ marginBottom: '8px' }}>
              What would you like to send?
            </FormLabel>
            <Grid container flexDirection="column">
              {pdfTypeOptions.map(docType => (
                <FormControlLabel
                  key={docType}
                  control={
                    <Checkbox
                      checked={selectedDocuments.includes(docType)}
                      onChange={() => handlePdfTypeChange(docType)}
                    />
                  }
                  label={pdfTypeNames[docType]}
                />
              ))}
            </Grid>
          </Grid>
        )}
        <FormControlLabel
          style={{ marginBottom: '8px' }}
          control={<Checkbox checked={includeImages} onChange={event => setIncludeImages(event.target.checked)} />}
          label="Include Images"
        />
        <Grid item>
          <FormLabel component="legend">Select recipients</FormLabel>
          <FormControlLabel
            control={
              <Checkbox disabled={recipients.length === 0} checked={selectAll} onChange={handleSelectAllChange} />
            }
            label="Select / Deselect All"
          />
          <Grid container flexDirection="column">
            {recipients.map(email => (
              <FormControlLabel
                key={email}
                control={
                  <Checkbox
                    checked={selectedRecipients.includes(email)}
                    onChange={() => handleRecipientChange(email)}
                  />
                }
                label={email}
              />
            ))}
            {recipients.length === 0 && (
              <Typography variant="caption">No emails available for this customer</Typography>
            )}
            {/* Custom email option */}
            <FormControlLabel
              control={<Checkbox checked={isCustomSelected} onChange={toggleCustomEmail} />}
              label="Custom"
            />
            {isCustomSelected && (
              <TextField
                label="Custom Email"
                value={customEmail}
                onChange={handleCustomEmailChange}
                fullWidth
                size="small"
                error={customEmail !== '' && !isValidEmail(customEmail)}
                helperText={customEmail !== '' && !isValidEmail(customEmail) ? 'Invalid email address' : undefined}
                margin="normal"
                type="email"
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSendEmail} color="primary" variant="contained" disabled={!isFormValid()}>
          {isLoading ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailModal
